<template>
    <div class="space-y-6">
        <section
            v-if="selectedJourneyReferencesExistingGroups || isEditingJourney"
            class="space-y-3 border-t border-line pt-5"
        >
            <h3 class="font-semibold text-primary">Change current journey</h3>

            <div
                v-if="selectedJourneyReferencesExistingGroups"
                class="flex items-center justify-between gap-3 rounded-lg bg-surface-muted p-3"
            >
                <span class="min-w-0 grow truncate">
                    <JourneyLabel
                        :details="getJourneyLabelDetails(selectedJourney!, stationGroups)"
                    />
                </span>
                <button
                    v-if="selectedJourney && selectedJourneyReferencesExistingGroups"
                    class="appButton appButton--secondary shrink-0 py-1"
                    type="button"
                    @click="toggleJourneyEditor"
                >
                    {{ isEditingJourney ? "Done" : "Edit journey" }}
                </button>
            </div>

            <div
                v-if="isEditingJourney && selectedJourneyIndex !== -1"
                class="space-y-3 border-l-2 border-primary pl-3"
            >
                <JourneySettingsFields
                    v-model:journey="journeys[selectedJourneyIndex]!"
                    :stationGroups="stationGroups"
                    :journeys="journeys"
                    :scheduleNames="getScheduleNamesThatUseJourney(selectedJourney!.id, schedules)"
                    @changed="emit('changed')"
                />

                <button
                    v-if="isEditingJourney"
                    class="appButton appButton--secondary"
                    @click="isEditingJourney = !isEditingJourney"
                >
                    Cancel
                </button>

            </div>
        </section>

        <section
            v-if="!isEditingJourney"
            class="space-y-3 border-t border-line pt-5"
        >
            <h3 class="font-semibold text-primary">Choose a different journey</h3>
            <label class="block">
                <span class="mb-1 block text-xs text-ink-subtle"> Use an existing journey </span>
                <select v-model="selectedJourneyId" class="appInput">
                    <option value="">Choose a journey</option>
                    <option
                        v-for="journey in selectableJourneys"
                        :key="journey.id"
                        :value="journey.id"
                    >
                        {{ getJourneySettingsLabel(journey, stationGroups) }}
                    </option>
                </select>
            </label>
            <div class="flex flex-wrap gap-2">
                <button
                    class="appButton appButton--primary py-1.5"
                    type="button"
                    :disabled="selectedJourneyId === ''"
                    @click="useSelectedJourney"
                >
                    Use journey
                </button>
                <button
                    class="appButton appButton--secondary py-1.5"
                    type="button"
                    @click="createJourney"
                >
                    Create a new journey
                </button>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import {journeyReferencesExistingGroups} from "../../../journeys/journeyIdentity";
import {computed, ref} from "vue";
import type {DisplaySchedule} from "../../../dto/displaySchedule.dto";
import type {Journey} from "../../../dto/journey.dto";
import type {StationGroup} from "../../../dto/stationGroup.dto";
import {getJourneyLabelDetails} from "../../../journeys/journeyLabels";
import JourneyLabel from "../../journeys/JourneyLabel.vue";
import JourneySettingsFields from "../journeys/JourneySettingsFields.vue";
import {
    createEmptyJourney,
    getJourneySettingsLabel,
    getScheduleNamesThatUseJourney,
} from "./scheduleSettings";

const props = defineProps<{
    stationGroups: StationGroup[];
    schedules: DisplaySchedule[];
}>();

const schedule = defineModel<DisplaySchedule>("schedule", {required: true});
const journeys = defineModel<Journey[]>("journeys", {required: true});
const isEditingJourney = ref(false);
const selectedJourneyId = ref("");

const emit = defineEmits<{
    changed: [];
}>();

const selectedJourney = computed(() =>
    journeys.value.find((journey) => journey.id === schedule.value.journeyId)
);
const selectedJourneyReferencesExistingGroups = computed(
    () =>
        selectedJourney.value !== undefined &&
        journeyReferencesExistingGroups(selectedJourney.value, props.stationGroups)
);
const selectedJourneyIndex = computed(() =>
    journeys.value.findIndex((journey) => journey.id === schedule.value.journeyId)
);
const selectableJourneys = computed(() =>
    journeys.value.filter(
        (journey) =>
            journey.id !== schedule.value.journeyId &&
            journeyReferencesExistingGroups(journey, props.stationGroups)
    )
);

function useSelectedJourney(): void {
    if (!selectedJourneyId.value) {
        return;
    }

    setJourney(selectedJourneyId.value);
    selectedJourneyId.value = "";
    isEditingJourney.value = false;
}

function setJourney(journeyId: string): void {
    const previousJourney = selectedJourney.value;

    schedule.value.journeyId = journeyId;
    removeUnusedIncompleteJourney(previousJourney);
    emit("changed");
}

function createJourney(): void {
    const journey = createEmptyJourney();
    journeys.value.push(journey);
    setJourney(journey.id);
    isEditingJourney.value = true;
}

function toggleJourneyEditor(): void {
    isEditingJourney.value = !isEditingJourney.value;
}

function removeUnusedIncompleteJourney(journey: Journey | undefined): void {
    if (
        !journey ||
        journeyReferencesExistingGroups(journey, props.stationGroups) ||
        props.schedules.some(
            (candidate) => candidate.id !== schedule.value.id && candidate.journeyId === journey.id
        )
    ) {
        return;
    }

    journeys.value = journeys.value.filter((candidate) => candidate.id !== journey.id);
}
</script>
